// Пагинацията под таблицата: диапазонът, изборът на брой редове, номерата
// на страниците и назад/напред.
//
// Номерата ги има, защото само с „Назад / Напред“ скокът до последната
// страница на дълъг период е въпрос на десет клика. Показват се най-много
// седем бутона: първата, последната и прозорец около текущата, с многоточие
// между тях.

import { Select } from "antd";
import { useTranslation } from "react-i18next";

type Props = {
  pageIndex: number;
  pageSize: number;
  pageSizeOptions: number[];
  rowCount: number;
  onPageIndexChange: (pageIndex: number) => void;
  onPageSizeChange: (pageSize: number) => void;
};

const navButtonClass =
  "h-8 rounded-lg border border-shade-3 bg-surface px-3 text-[12px] font-bold text-ink transition-colors hover:bg-fill disabled:cursor-default disabled:opacity-40 disabled:hover:bg-surface";

/** Номерата на страниците около текущата; `null` е многоточие. */
const pageWindow = (
  pageIndex: number,
  lastPageIndex: number,
): (number | null)[] => {
  if (lastPageIndex <= 0) return [0];
  if (lastPageIndex <= 6) {
    return Array.from({ length: lastPageIndex + 1 }, (_, index) => index);
  }
  const around = [pageIndex - 1, pageIndex, pageIndex + 1].filter(
    (index) => index > 0 && index < lastPageIndex,
  );
  const pages: (number | null)[] = [0];
  if (around.length && around[0] > 1) pages.push(null);
  pages.push(...around);
  if (around.length && around[around.length - 1] < lastPageIndex - 1) {
    pages.push(null);
  }
  pages.push(lastPageIndex);
  return pages;
};

export const Pager = ({
  pageIndex,
  pageSize,
  pageSizeOptions,
  rowCount,
  onPageIndexChange,
  onPageSizeChange,
}: Props) => {
  const { t } = useTranslation();

  const from = rowCount === 0 ? 0 : pageIndex * pageSize + 1;
  const to = Math.min(rowCount, (pageIndex + 1) * pageSize);
  const lastPageIndex = Math.max(0, Math.ceil(rowCount / pageSize) - 1);

  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-shade-2 px-4 py-2.5">
      <span className="text-[12px] font-semibold text-soft">
        {t("dataTable.pageRange", { from, to, total: rowCount })}
      </span>

      <Select
        value={pageSize}
        onChange={onPageSizeChange}
        size="small"
        variant="borderless"
        className="!text-[12px]"
        popupMatchSelectWidth={false}
        options={pageSizeOptions.map((option) => ({
          value: option,
          label: t("dataTable.perPage", { count: option }),
        }))}
      />

      <div className="flex-1" />

      <div className="flex items-center gap-2">
        <button
          type="button"
          className={navButtonClass}
          disabled={pageIndex <= 0}
          onClick={() => onPageIndexChange(pageIndex - 1)}
        >
          {`‹ ${t("dataTable.prev")}`}
        </button>
        {lastPageIndex > 0 ? (
          <div className="flex items-center gap-1">
            {pageWindow(pageIndex, lastPageIndex).map((page, index) =>
              page === null ? (
                <span
                  key={`gap-${index}`}
                  aria-hidden
                  className="px-0.5 text-[12px] font-bold text-faint"
                >
                  …
                </span>
              ) : (
                <button
                  key={page}
                  type="button"
                  aria-label={t("dataTable.goToPage", { page: page + 1 })}
                  aria-current={page === pageIndex ? "page" : undefined}
                  onClick={() => onPageIndexChange(page)}
                  className={`h-8 min-w-8 rounded-lg border px-2 font-pos-mono text-[12px] font-bold tabular-nums transition-colors ${
                    page === pageIndex
                      ? "border-brand-tint2 bg-brand-tint text-brand-deep-ink"
                      : "border-shade-3 bg-surface text-ink hover:bg-fill"
                  }`}
                >
                  {page + 1}
                </button>
              ),
            )}
          </div>
        ) : null}

        <button
          type="button"
          className={navButtonClass}
          disabled={pageIndex >= lastPageIndex}
          onClick={() => onPageIndexChange(pageIndex + 1)}
        >
          {`${t("dataTable.next")} ›`}
        </button>
      </div>
    </div>
  );
};
